import { useState, useEffect, useCallback } from 'react';

export interface MockConfig {
  timeout?: number;
  isSuccess?: boolean;
  isFailure?: boolean;
  watcher?: any;
}

/**
 * This method is used when we don't have API's ready on graphql layer to fetch the data
 * In case we simply use mockData in component itself, we won't be able to test the different cases of responses
 * For testing the component, you can pass the config so that error or response can be tested easily
 * You can easily mock the "useQueryMock" method in test cases
 * @param content
 * @param config
 * e.g.
 * 1. Return Success Response in 2000ms
 * const {loading, data, error} = useQueryMock<MockPartnerAccount, string>(mockData);
 *
 * 2. Return Error Response in 2000ms
 * const {loading, data, error} = useQueryMock<MockPartnerAccount, string>("This is error", {
 *     isFailure: true
 * });
 *
 * 3. Return Loading Permanently
 * const {loading, data, error} = useQueryMock<MockPartnerAccount, string>("", {
 *     isLoading: true
 * });
 *
 * 4. Return Success response with Custom Delay in ms
 * const {loading, data, error} = useQueryMock<MockPartnerAccount, string>(mockData, {
 *     timeout: 5000
 * });
 *
 *
 */
function useQueryMock<T, Q>(content: T | Q, config?: MockConfig) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Q>();
  const [loading, setLoading] = useState(false);

  const handleError = () => {
    setError(content as Q);
    setLoading(false);
  };

  const runQuery = useCallback(() => {
    const handleSuccess = () => {
      setData(content as T);
      setLoading(false);
    };

    setLoading(true);

    setTimeout(() => {
      if (!config?.isFailure) {
        handleSuccess();
      } else {
        handleError();
      }
    }, config?.timeout || 2000);
  }, [config && config.watcher]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  return { data, loading, error, refetch: runQuery };
}
export { useQueryMock };
